import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useQuery } from '@tanstack/react-query';

import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  LinearProgress,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';

import CloseRoundedIcon
  from '@mui/icons-material/CloseRounded';

import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import {
  getMarginCenterProductCustomerPortfolioDetail,
} from '../services/marginCenterService';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterProductCustomerPortfolioConcentrationChart,
  MarginCenterProductCustomerPortfolioConcentrationRow,
  MarginCenterProductCustomerPortfolioConcentrationSummary,
  MarginCenterProductCustomerPortfolioCoreBucket,
  MarginCenterProductCustomerPortfolioCoreRow,
  MarginCenterProductCustomerPortfolioCoreSummary,
  MarginCenterProductCustomerPortfolioDetailParams,
  MarginCenterProductCustomerPortfolioDetailPeriod,
  MarginCenterProductCustomerPortfolioDetailType,
  MarginCenterProductCustomerPortfolioMixBucket,
  MarginCenterProductCustomerPortfolioMixRow,
  MarginCenterProductCustomerPortfolioMixSummary,
  MarginCenterProductCustomerPortfolioRotationBucket,
  MarginCenterProductCustomerPortfolioRotationRow,
  MarginCenterProductCustomerPortfolioRotationSummary,
} from '../types/productCustomerPortfolioDetail';
import { pricingNumberFormat } from '../../../modules/pricingJourney/formatNumber';


interface ProductCustomerPortfolioDetailModalProps {
  open: boolean;

  detail: MarginCenterProductCustomerPortfolioDetailType;

  productId: string;

  startDate?: string | null;
  endDate?: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate?: string | null;
  comparisonEndDate?: string | null;

  currencyCode: string | null;

  onOpenRelationship: (customerId: string) => void;
  onClose: () => void;
}


const DONUT_COLORS = [
  '#38bdf8',
  '#34d399',
  '#fbbf24',
  '#a78bfa',
  '#fb7185',
  '#22d3ee',
  '#4ade80',
  '#f59e0b',
  '#818cf8',
  '#f472b6',
  '#64748b',
];


const numberFormatter = pricingNumberFormat({
  maximumFractionDigits: 2,
});

const integerFormatter = pricingNumberFormat({
  maximumFractionDigits: 0,
});


function formatNumber(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return numberFormatter.format(value);
}


function formatInteger(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return integerFormatter.format(value);
}


function formatPct(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  return `${formatNumber(value)} %`;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (value > 0) {
    return `+${formatNumber(value)} pp`;
  }

  if (value < 0) {
    return `−${formatNumber(
      Math.abs(value),
    )} pp`;
  }

  return '0 pp';
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (value === null || value === undefined) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return pricingNumberFormat({
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return formatNumber(value);
  }
}


function movementColor(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return '#94a3b8';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function calculatePositionDelta(
  currentPosition: number | null,
  comparisonPosition: number | null,
): number | null {
  if (
    currentPosition === null
    || comparisonPosition === null
  ) {
    return null;
  }

  return comparisonPosition
    - currentPosition;
}


function formatSignedPositions(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (value > 0) {
    return `+${formatInteger(value)} pos.`;
  }

  if (value < 0) {
    return `−${formatInteger(
      Math.abs(value),
    )} pos.`;
  }

  return '0 pos.';
}


function calculateRevenueDelta(
  currentRevenue: number | null | undefined,
  comparisonRevenue: number | null | undefined,
): number | null {
  if (
    currentRevenue === null
    || currentRevenue === undefined
    || comparisonRevenue === null
    || comparisonRevenue === undefined
  ) {
    return null;
  }

  return currentRevenue
    - comparisonRevenue;
}


function formatSignedCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (value > 0) {
    return `+${formatCurrency(
      value,
      currencyCode,
    )}`;
  }

  if (value < 0) {
    return `−${formatCurrency(
      Math.abs(value),
      currencyCode,
    )}`;
  }

  return formatCurrency(
    0,
    currencyCode,
  );
}


function CurrentComparisonValue({
  current,
  comparison,
}: {
  current: string;
  comparison: string;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'flex-end',
        gap: 0.55,
        whiteSpace: 'nowrap',
      }}
    >
      <Typography
        component="span"
        variant="body2"
        sx={{
          color: '#e2e8f0',
          fontWeight: 800,
        }}
      >
        {current}
      </Typography>

      <Typography
        component="span"
        variant="body2"
        sx={{
          color: '#475569',
          fontWeight: 700,
        }}
      >
        /
      </Typography>

      <Typography
        component="span"
        variant="body2"
        sx={{
          color: '#64748b',
          fontWeight: 700,
        }}
      >
        {comparison}
      </Typography>
    </Box>
  );
}

function getRotationCadencePresentation(
  row: MarginCenterProductCustomerPortfolioRotationRow,
): {
  primary: string;
  secondary: string;
} {
  const behavior = row.behavior;
  const cadence = behavior.cadence;

  if (
    behavior.status !== 'ok'
    || !cadence
  ) {
    return {
      primary: 'Sin histórico previo',
      secondary: 'Cadencia no evaluable',
    };
  }

  if (cadence.sample_status === 'no_history') {
    return {
      primary: 'Sin histórico previo',
      secondary: 'Sin intervalos de compra',
    };
  }

  if (cadence.sample_status === 'single_purchase') {
    return {
      primary: 'Una compra previa',
      secondary: 'Sin intervalo completo',
    };
  }

  if (cadence.sample_status === 'limited') {
    return {
      primary: 'Histórico limitado',
      secondary:
        `${formatInteger(
          cadence.completed_interval_count,
        )} intervalos · mediana ${
          formatNumber(
            cadence.median_interval_days,
          )
        } d`,
    };
  }

  return {
    primary:
      `Mediana ${
        formatNumber(
          cadence.median_interval_days,
        )
      } d`,

    secondary:
      `P75 ${
        formatNumber(
          cadence.p75_interval_days,
        )
      } d · P90 ${
        formatNumber(
          cadence.p90_interval_days,
        )
      } d`,
  };
}


function getRotationAbsencePresentation(
  row: MarginCenterProductCustomerPortfolioRotationRow,
): {
  primary: string;
  secondary: string;
  color: string;
} {
  const behavior = row.behavior;
  const cadence = behavior.cadence;
  const recency = behavior.recency;

  if (
    behavior.status !== 'ok'
    || !recency
  ) {
    return {
      primary: 'No evaluable',
      secondary: 'Sin histórico previo',
      color: '#64748b',
    };
  }

  const days =
    recency.days_since_last_purchase;

  const primary =
    days === null
      ? 'Sin compra previa'
      : `${formatInteger(days)} d sin compra`;

  if (recency.cadence_band === 'within_p75') {
    return {
      primary,
      secondary:
        `Dentro de P75${
          cadence?.p75_interval_days !== null
          && cadence?.p75_interval_days !== undefined
            ? ` (${formatNumber(
                cadence.p75_interval_days,
              )} d)`
            : ''
        }`,
      color: '#e2e8f0',
    };
  }

  if (
    recency.cadence_band
    === 'between_p75_p90'
  ) {
    return {
      primary,
      secondary:
        `Entre P75 y P90${
          cadence?.p90_interval_days !== null
          && cadence?.p90_interval_days !== undefined
            ? ` · P90 ${formatNumber(
                cadence.p90_interval_days,
              )} d`
            : ''
        }`,
      color: '#fbbf24',
    };
  }

  if (recency.cadence_band === 'above_p90') {
    return {
      primary,
      secondary:
        `Por encima de P90${
          cadence?.p90_interval_days !== null
          && cadence?.p90_interval_days !== undefined
            ? ` (${formatNumber(
                cadence.p90_interval_days,
              )} d)`
            : ''
        }`,
      color: '#fb7185',
    };
  }

  if (
    recency.cadence_band
    === 'insufficient_intervals'
  ) {
    return {
      primary,
      secondary: 'Histórico insuficiente',
      color: '#94a3b8',
    };
  }

  return {
    primary,
    secondary: 'Sin histórico previo',
    color: '#64748b',
  };
}


function getRotationPurchaseSizePresentation(
  row: MarginCenterProductCustomerPortfolioRotationRow,
): {
  primary: string;
  secondary: string;
  detail: string;
} {
  const behavior = row.behavior;

  const active =
    behavior.active_period;

  const history =
    behavior.purchase_size_history;

  if (
    behavior.status !== 'ok'
    || !active
    || active.median_units_per_purchase_day === null
  ) {
    return {
      primary: 'Cantidad no disponible',
      secondary:
        'No se puede comparar el tamaño de compra',
      detail: '—',
    };
  }

  const activeMedian =
    active.median_units_per_purchase_day;

  const activeDetail =
    `${formatNumber(
      active.units,
    )} uds. · ${
      formatInteger(
        active.purchase_days,
      )
    } ${
      active.purchase_days === 1
        ? 'día de compra'
        : 'días de compra'
    }`;

  if (
    !history
    || history.purchase_event_count === 0
    || history.median_units_per_purchase_day === null
  ) {
    return {
      primary:
        `${formatNumber(
          activeMedian,
        )} uds./día`,
      secondary:
        'Sin histórico comparable',
      detail:
        activeDetail,
    };
  }

  const historicalMedian =
    history.median_units_per_purchase_day;

  const delta =
    active.median_vs_historical_median_delta_pct;

  if (history.purchase_event_count === 1) {
    return {
      primary:
        `${formatNumber(
          activeMedian,
        )} / ${formatNumber(
          historicalMedian,
        )} uds./día`,
      secondary:
        'tramo / 1 compra histórica',
      detail:
        activeDetail,
    };
  }

  return {
    primary:
      `${formatNumber(
        activeMedian,
      )} / ${formatNumber(
        historicalMedian,
      )} uds./día`,

    secondary:
      `tramo / mediana histórica${
        delta === null
          ? ''
          : ` · ${
              delta > 0
                ? '+'
                : delta < 0
                  ? '−'
                  : ''
            }${formatNumber(
              Math.abs(delta),
            )} %`
      }`,

    detail:
      activeDetail,
  };
}

function detailTitle(
  detail: MarginCenterProductCustomerPortfolioDetailType,
): string {
  if (detail === 'concentration') {
    return 'Desglose de concentración';
  }

  if (detail === 'core_movement') {
    return 'Cambios del núcleo de ingresos';
  }

  if (detail === 'mix_movement') {
    return 'Cambios de mix';
  }

  return 'Rotación de actividad';
}


function concentrationPeriodLabel(
  period: MarginCenterProductCustomerPortfolioDetailPeriod,
): string {
  return period === 'current'
    ? 'Periodo actual'
    : 'Periodo comparado';
}


function coreBucketLabel(
  bucket: MarginCenterProductCustomerPortfolioCoreBucket,
): string {
  if (bucket === 'stayed') {
    return 'Permanecen';
  }

  if (bucket === 'joined') {
    return 'Entran';
  }

  return 'Salen';
}


export default function ProductCustomerPortfolioDetailModal({
  open,
  detail,
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
  onOpenRelationship,
  onClose,
}: ProductCustomerPortfolioDetailModalProps) {
  const [
    activePeriod,
    setActivePeriod,
  ] = useState<
    MarginCenterProductCustomerPortfolioDetailPeriod
  >('current');

  const [
    activeBucket,
    setActiveBucket,
  ] = useState<
    MarginCenterProductCustomerPortfolioCoreBucket
  >('stayed');

  const [
    activeMixBucket,
    setActiveMixBucket,
  ] = useState<
    MarginCenterProductCustomerPortfolioMixBucket
  >('gaining');

  const [
    activeRotationBucket,
    setActiveRotationBucket,
  ] = useState<
    MarginCenterProductCustomerPortfolioRotationBucket
  >('current_only');

  const [
    cursor,
    setCursor,
  ] = useState<string | undefined>(
    undefined,
  );

  const [
    cursorHistory,
    setCursorHistory,
  ] = useState<string[]>([]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setActivePeriod('current');
    setActiveBucket('stayed');
    setActiveMixBucket('gaining');
    setActiveRotationBucket('current_only');
    setCursor(undefined);
    setCursorHistory([]);
  }, [
    detail,
    open,
  ]);

  const resetPagination = () => {
    setCursor(undefined);
    setCursorHistory([]);
  };

  const params =
    useMemo<MarginCenterProductCustomerPortfolioDetailParams>(
      () => ({
        start_date:
          startDate ?? undefined,

        end_date:
          endDate ?? undefined,

        comparison_mode:
          comparisonMode,

        comparison_start_date:
          comparisonMode === 'custom'
            ? comparisonStartDate ?? undefined
            : undefined,

        comparison_end_date:
          comparisonMode === 'custom'
            ? comparisonEndDate ?? undefined
            : undefined,

        detail,

        period:
          detail === 'concentration'
            ? activePeriod
            : 'current',

        core_bucket:
          detail === 'core_movement'
            ? activeBucket
            : undefined,

        detail_bucket:
          detail === 'mix_movement'
            ? activeMixBucket
            : detail === 'activity_rotation'
              ? activeRotationBucket
              : undefined,

        limit: 25,
        cursor,
      }),
      [
        activeBucket,
        activeMixBucket,
        activePeriod,
        activeRotationBucket,
        comparisonEndDate,
        comparisonMode,
        comparisonStartDate,
        cursor,
        detail,
        endDate,
        startDate,
      ],
    );

  const detailQuery = useQuery({
    queryKey: [
      'margin-center',
      'product-customer-portfolio-detail',
      productId,
      params,
    ],
    queryFn: () =>
      getMarginCenterProductCustomerPortfolioDetail(
        productId,
        params,
      ),
    enabled:
      open
      && Boolean(productId),
    placeholderData: (previousData) => {
      if (
        !previousData
        || previousData.detail !== detail
      ) {
        return undefined;
      }

      if (detail === 'concentration') {
        return previousData.period === activePeriod
          ? previousData
          : undefined;
      }

      if (detail === 'core_movement') {
        return previousData.core_bucket === activeBucket
          ? previousData
          : undefined;
      }

      if (detail === 'mix_movement') {
        return previousData.detail_bucket
          === activeMixBucket
          ? previousData
          : undefined;
      }

      return previousData.detail_bucket
        === activeRotationBucket
        ? previousData
        : undefined;
    },
    retry: false,
  });

  const data = detailQuery.data;

  const concentrationSummary =
    detail === 'concentration'
      ? (
          data?.summary as
            | MarginCenterProductCustomerPortfolioConcentrationSummary
            | null
            | undefined
        )
      : null;

  const concentrationChart =
    detail === 'concentration'
      ? (
          data?.chart as
            | MarginCenterProductCustomerPortfolioConcentrationChart
            | null
            | undefined
        )
      : null;

  const concentrationRows =
    detail === 'concentration'
      ? (
          data?.rows as
            | MarginCenterProductCustomerPortfolioConcentrationRow[]
            | undefined
        )
      : undefined;

  const coreSummary =
    detail === 'core_movement'
      ? (
          data?.summary as
            | MarginCenterProductCustomerPortfolioCoreSummary
            | null
            | undefined
        )
      : null;

  const coreRows =
    detail === 'core_movement'
      ? (
          data?.rows as
            | MarginCenterProductCustomerPortfolioCoreRow[]
            | undefined
        )
      : undefined;

  const mixSummary =
    detail === 'mix_movement'
      ? (
          data?.summary as
            | MarginCenterProductCustomerPortfolioMixSummary
            | null
            | undefined
        )
      : null;

  const mixRows =
    detail === 'mix_movement'
      ? (
          data?.rows as
            | MarginCenterProductCustomerPortfolioMixRow[]
            | undefined
        )
      : undefined;

  const rotationSummary =
    detail === 'activity_rotation'
      ? (
          data?.summary as
            | MarginCenterProductCustomerPortfolioRotationSummary
            | null
            | undefined
        )
      : null;

  const rotationRows =
    detail === 'activity_rotation'
      ? (
          data?.rows as
            | MarginCenterProductCustomerPortfolioRotationRow[]
            | undefined
        )
      : undefined;

  const donutData =
    useMemo(
      () => {
        if (!concentrationChart) {
          return [];
        }

        const segments =
          concentrationChart.segments.map(
            (segment) => ({
              name: segment.label,
              value:
                segment.share_within_core_pct,
            }),
          );

        if (
          concentrationChart
            .other_core_share_pct > 0
        ) {
          segments.push({
            name: 'Otros del núcleo',
            value:
              concentrationChart
                .other_core_share_pct,
          });
        }

        return segments;
      },
      [concentrationChart],
    );

  const nextPage = () => {
    const nextCursor =
      data?.page.next_cursor;

    if (!nextCursor) {
      return;
    }

    setCursorHistory((current) => [
      ...current,
      cursor ?? '',
    ]);

    setCursor(nextCursor);
  };

  const previousPage = () => {
    if (cursorHistory.length === 0) {
      return;
    }

    const previousCursor =
      cursorHistory[
        cursorHistory.length - 1
      ];

    setCursorHistory((current) =>
      current.slice(0, -1)
    );

    setCursor(
      previousCursor || undefined,
    );
  };

  const openRelationship = (
    customerId: string,
  ) => {
    onClose();
    onOpenRelationship(customerId);
  };

  const subtitle =
    detail === 'concentration'
      ? `${concentrationPeriodLabel(
          activePeriod,
        )} · clientes que forman el núcleo del 80 % de ingresos`
      : detail === 'core_movement'
        ? `${coreBucketLabel(
            activeBucket,
          )} · cambio observado de pertenencia al núcleo`
        : detail === 'mix_movement'
          ? activeMixBucket === 'gaining'
            ? 'Clientes comparables que ganan peso en los ingresos del producto'
            : 'Clientes comparables que pierden peso en los ingresos del producto'
          : activeRotationBucket === 'current_only'
            ? 'Clientes con actividad actual y sin actividad en el periodo comparado'
            : 'Clientes con actividad comparada y sin actividad en el periodo actual';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: {
            xs: '98vw',
            md: '96vw',
            xl: 1500,
          },
          maxWidth: '98vw',
          height: {
            xs: '92vh',
            md: '92vh',
          },
          maxHeight: '94vh',
          bgcolor: '#0b1220',
          border:
            '1px solid rgba(148,163,184,0.18)',
          borderRadius: 3,
          boxShadow:
            '0 24px 80px rgba(2,6,23,0.56)',
          overflow: 'hidden',
        },
      }}
    >
      <DialogContent
        sx={{
          height: '100%',
          boxSizing: 'border-box',
          overflow: 'hidden',
          p: {
            xs: 2,
            md: 2.5,
          },
        }}
      >
        <Stack
          spacing={1.25}
          sx={{
            height: '100%',
            minHeight: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {detailTitle(detail)}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  mt: 0.25,
                  color: '#94a3b8',
                }}
              >
                {subtitle}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              aria-label="Cerrar"
              sx={{
                color: '#94a3b8',
                flexShrink: 0,
              }}
            >
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          {detail === 'concentration' ? (
            <Tabs
              value={activePeriod}
              onChange={(
                _event,
                nextValue:
                  MarginCenterProductCustomerPortfolioDetailPeriod,
              ) => {
                setActivePeriod(nextValue);
                resetPagination();
              }}
              sx={{
                minHeight: 34,

                '& .MuiTab-root': {
                  minHeight: 34,
                  py: 0.5,
                  textTransform: 'none',
                },
              }}
            >
              <Tab
                value="current"
                label="Actual"
              />

              {comparisonMode !== 'none' && (
                <Tab
                  value="comparison"
                  label="Comparación"
                />
              )}
            </Tabs>
          ) : detail === 'core_movement' ? null
          : detail === 'mix_movement' ? (
            <Tabs
              value={activeMixBucket}
              onChange={(
                _event,
                nextValue:
                  MarginCenterProductCustomerPortfolioMixBucket,
              ) => {
                setActiveMixBucket(nextValue);
                resetPagination();
              }}
              sx={{
                minHeight: 34,

                '& .MuiTab-root': {
                  minHeight: 34,
                  py: 0.5,
                  textTransform: 'none',
                },
              }}
            >
              <Tab
                value="gaining"
                label="Ganan peso"
              />

              <Tab
                value="losing"
                label="Pierden peso"
              />
            </Tabs>
          ) : null}

          {detailQuery.isFetching && (
            <LinearProgress />
          )}

          {detailQuery.isError && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Alert
                severity="error"
                sx={{
                  width: '100%',
                  maxWidth: 680,
                }}
              >
                No se ha podido cargar este desglose de cartera.
              </Alert>
            </Box>
          )}

          {!detailQuery.isError
            && !detailQuery.isFetching
            && data
            && data.status !== 'ok' && (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <Alert
                severity="info"
                sx={{
                  width: '100%',
                  maxWidth: 720,
                }}
              >
                Este desglose no es calculable para la selección actual.
              </Alert>
            </Box>
          )}

          {!detailQuery.isError
            && data?.status === 'ok'
            && detail === 'concentration'
            && concentrationSummary
            && concentrationChart && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: '300px minmax(0, 1fr)',
                  },
                  gap: 1.75,
                  minHeight: {
                    xs: 200,
                    md: 200,
                  },
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    minHeight: 195,
                  }}
                >
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                  >
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        innerRadius={62}
                        outerRadius={92}
                        paddingAngle={1}
                        stroke="none"
                      >
                        {donutData.map(
                          (entry, index) => (
                            <Cell
                              key={`${entry.name}-${index}`}
                              fill={
                                DONUT_COLORS[
                                  index
                                  % DONUT_COLORS.length
                                ]
                              }
                            />
                          ),
                        )}
                      </Pie>

                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>

                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'grid',
                      placeItems: 'center',
                      pointerEvents: 'none',
                    }}
                  >
                    <Box
                      sx={{
                        textAlign: 'center',
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: '#f8fafc',
                          fontWeight: 900,
                        }}
                      >
                        {formatInteger(
                          concentrationSummary
                            .core_customers,
                        )}
                      </Typography>

                      <Typography
                        variant="caption"
                        sx={{
                          color: '#64748b',
                        }}
                      >
                        clientes
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Stack
                  spacing={1}
                  sx={{
                    minWidth: 0,
                    justifyContent: 'center',
                  }}
                >
                  <Stack
                    direction={{
                      xs: 'column',
                      sm: 'row',
                    }}
                    spacing={{
                      xs: 0.25,
                      sm: 2,
                    }}
                    alignItems={{
                      xs: 'flex-start',
                      sm: 'baseline',
                    }}
                    useFlexGap
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: '#f8fafc',
                        fontWeight: 900,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatPct(
                        concentrationSummary
                          .core_revenue_share_pct,
                      )} de los ingresos
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 750,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatInteger(
                        concentrationSummary
                          .core_customers,
                      )} de {formatInteger(
                        concentrationSummary
                          .active_customers,
                      )} clientes
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm:
                          'repeat(2, minmax(0, 1fr))',
                        lg:
                          'repeat(3, minmax(0, 1fr))',
                      },
                      columnGap: 1.5,
                      rowGap: 0.35,
                    }}
                  >
                    {donutData.map(
                      (entry, index) => (
                        <Stack
                          key={`${entry.name}-legend-${index}`}
                          direction="row"
                          spacing={0.75}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              flexShrink: 0,
                              bgcolor:
                                DONUT_COLORS[
                                  index
                                  % DONUT_COLORS.length
                                ],
                            }}
                          />

                          <Typography
                            variant="caption"
                            noWrap
                            sx={{
                              color: '#94a3b8',
                              minWidth: 0,
                              flex: 1,
                            }}
                          >
                            {entry.name}
                          </Typography>

                          <Typography
                            variant="caption"
                            sx={{
                              color: '#e2e8f0',
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatPct(entry.value)}
                          </Typography>
                        </Stack>
                      ),
                    )}
                  </Box>
                </Stack>
              </Box>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  border:
                    '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 2.5,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 820,

                    '& .MuiTableCell-head': {
                      bgcolor: '#111827',
                      color: '#e2e8f0',
                      fontWeight: 850,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        #
                      </TableCell>

                      <TableCell>
                        Cliente
                      </TableCell>

                      <TableCell align="right">
                        Ingresos
                      </TableCell>

                      <TableCell align="right">
                        Peso
                      </TableCell>

                      <TableCell align="right">
                        Acumulado
                      </TableCell>

                      <TableCell align="right">
                        Peso dentro del núcleo
                      </TableCell>

                      <TableCell align="right">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(concentrationRows ?? []).map(
                      (row) => (
                        <TableRow
                          key={row.customer_id}
                        >
                          <TableCell>
                            {formatInteger(
                              row.position,
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 800,
                              }}
                            >
                              {row.label}
                            </Typography>

                            {row.is_boundary_customer && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  color: '#38bdf8',
                                }}
                              >
                                Cliente que cruza el umbral del 80 %
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                            sx={{
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatCurrency(
                              row.revenue,
                              currencyCode,
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {formatPct(
                              row.revenue_share_pct,
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {formatPct(
                              row.cumulative_revenue_share_pct,
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            {formatPct(
                              row.share_within_core_pct,
                            )}
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Button
                              size="small"
                              variant="text"
                              endIcon={
                                <OpenInNewRoundedIcon
                                  sx={{
                                    fontSize: 14,
                                  }}
                                />
                              }
                              onClick={() =>
                                openRelationship(
                                  row.customer_id,
                                )
                              }
                              sx={{
                                textTransform: 'none',
                              }}
                            >
                              Examinar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!detailQuery.isError
            && data?.status === 'ok'
            && detail === 'core_movement'
            && coreSummary && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm:
                      'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 0.75,
                }}
              >
                {[
                  {
                    bucket: 'stayed' as const,
                    label: 'Permanecen',
                    value:
                      coreSummary.stayed_count,
                    color: '#e2e8f0',
                  },
                  {
                    bucket: 'joined' as const,
                    label: 'Entran',
                    value:
                      coreSummary.joined_count,
                    color: '#34d399',
                  },
                  {
                    bucket: 'left' as const,
                    label: 'Salen',
                    value:
                      coreSummary.left_count,
                    color: '#fb7185',
                  },
                ].map((item) => {
                  const selected =
                    activeBucket === item.bucket;

                  return (
                    <Button
                      key={item.bucket}
                      variant="outlined"
                      aria-pressed={selected}
                      onClick={() => {
                        setActiveBucket(
                          item.bucket,
                        );
                        resetPagination();
                      }}
                      sx={{
                        px: 1.5,
                        py: 1,
                        minHeight: 62,
                        justifyContent:
                          'space-between',
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: selected
                          ? '#38bdf8'
                          : 'rgba(148,163,184,0.16)',
                        bgcolor: selected
                          ? 'rgba(56,189,248,0.10)'
                          : 'rgba(15,23,42,0.26)',
                        color: '#e2e8f0',

                        '&:hover': {
                          borderColor:
                            '#38bdf8',
                          bgcolor:
                            'rgba(56,189,248,0.08)',
                        },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: selected
                            ? '#f8fafc'
                            : '#94a3b8',
                          fontWeight: 800,
                        }}
                      >
                        {item.label}
                      </Typography>

                      <Typography
                        variant="h6"
                        sx={{
                          color: item.color,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {formatInteger(
                          item.value,
                        )}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  border:
                    '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 2.5,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 980,

                    '& .MuiTableCell-head': {
                      bgcolor: '#111827',
                      color: '#e2e8f0',
                      fontWeight: 850,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Cliente
                      </TableCell>

                      <TableCell align="right">
                        Posición
                      </TableCell>

                      <TableCell align="right">
                        Δ posición
                      </TableCell>

                      <TableCell align="right">
                        Peso
                      </TableCell>

                      <TableCell align="right">
                        Δ peso
                      </TableCell>

                      <TableCell align="right">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(coreRows ?? []).map(
                      (row) => (
                        <TableRow
                          key={row.customer_id}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 800,
                              }}
                            >
                              {row.label}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <CurrentComparisonValue
                              current={
                                row.current
                                  .revenue_position
                                  !== null
                                  ? `#${formatInteger(
                                      row.current
                                        .revenue_position,
                                    )}`
                                  : '—'
                              }
                              comparison={
                                row.comparison
                                  .revenue_position
                                  !== null
                                  ? `#${formatInteger(
                                      row.comparison
                                        .revenue_position,
                                    )}`
                                  : '—'
                              }
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    calculatePositionDelta(
                                      row.current
                                        .revenue_position,
                                      row.comparison
                                        .revenue_position,
                                    ),
                                  ),
                                fontWeight: 850,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatSignedPositions(
                                calculatePositionDelta(
                                  row.current
                                    .revenue_position,
                                  row.comparison
                                    .revenue_position,
                                ),
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <CurrentComparisonValue
                              current={formatPct(
                                row.current
                                  .revenue_share_pct,
                              )}
                              comparison={formatPct(
                                row.comparison
                                  .revenue_share_pct,
                              )}
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    row.revenue_share_delta_points,
                                  ),
                                fontWeight: 850,
                              }}
                            >
                              {formatSignedPoints(
                                row.revenue_share_delta_points,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Button
                              size="small"
                              variant="text"
                              endIcon={
                                <OpenInNewRoundedIcon
                                  sx={{
                                    fontSize: 14,
                                  }}
                                />
                              }
                              onClick={() =>
                                openRelationship(
                                  row.customer_id,
                                )
                              }
                              sx={{
                                textTransform: 'none',
                              }}
                            >
                              Examinar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!detailQuery.isError
            && data?.status === 'ok'
            && detail === 'mix_movement'
            && mixSummary && (
            <>
              <Stack
                direction="row"
                spacing={2.5}
                useFlexGap
                flexWrap="wrap"
                alignItems="center"
                sx={{
                  px: 0.25,
                  py: 0.25,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#e2e8f0',
                    fontWeight: 800,
                  }}
                >
                  {formatInteger(
                    mixSummary.continued_customers,
                  )} comparables
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#34d399',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mixSummary.gaining_share_customers,
                  )} ganan peso
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#fb7185',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mixSummary.losing_share_customers,
                  )} pierden peso
                </Typography>

                {mixSummary.unchanged_share_customers > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#94a3b8',
                      fontWeight: 750,
                    }}
                  >
                    {formatInteger(
                      mixSummary.unchanged_share_customers,
                    )} sin cambio
                  </Typography>
                )}
              </Stack>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  border:
                    '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 2.5,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1080,

                    '& .MuiTableCell-head': {
                      bgcolor: '#111827',
                      color: '#e2e8f0',
                      fontWeight: 850,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Cliente
                      </TableCell>

                      <TableCell align="right">
                        Posición
                      </TableCell>

                      <TableCell align="right">
                        Δ posición
                      </TableCell>

                      <TableCell align="right">
                        Ingresos
                      </TableCell>

                      <TableCell align="right">
                        Δ ingresos
                      </TableCell>

                      <TableCell align="right">
                        Peso
                      </TableCell>

                      <TableCell align="right">
                        Δ peso
                      </TableCell>

                      <TableCell align="right">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(mixRows ?? []).map(
                      (row) => (
                        <TableRow
                          key={row.customer_id}
                        >
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#f8fafc',
                                fontWeight: 800,
                              }}
                            >
                              {row.label}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <CurrentComparisonValue
                              current={
                                row.current
                                  .revenue_position
                                  !== null
                                  ? `#${formatInteger(
                                      row.current
                                        .revenue_position,
                                    )}`
                                  : '—'
                              }
                              comparison={
                                row.comparison
                                  .revenue_position
                                  !== null
                                  ? `#${formatInteger(
                                      row.comparison
                                        .revenue_position,
                                    )}`
                                  : '—'
                              }
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    calculatePositionDelta(
                                      row.current
                                        .revenue_position,
                                      row.comparison
                                        .revenue_position,
                                    ),
                                  ),
                                fontWeight: 850,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatSignedPositions(
                                calculatePositionDelta(
                                  row.current
                                    .revenue_position,
                                  row.comparison
                                    .revenue_position,
                                ),
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <CurrentComparisonValue
                              current={formatCurrency(
                                row.current.revenue,
                                currencyCode,
                              )}
                              comparison={formatCurrency(
                                row.comparison.revenue,
                                currencyCode,
                              )}
                            />
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    calculateRevenueDelta(
                                      row.current.revenue,
                                      row.comparison.revenue,
                                    ),
                                  ),
                                fontWeight: 850,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatSignedCurrency(
                                calculateRevenueDelta(
                                  row.current.revenue,
                                  row.comparison.revenue,
                                ),
                                currencyCode,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell
                            align="right"
                          >
                            <CurrentComparisonValue
                              current={formatPct(
                                row.current
                                  .revenue_share_pct,
                              )}
                              comparison={formatPct(
                                row.comparison
                                  .revenue_share_pct,
                              )}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                color:
                                  movementColor(
                                    row.revenue_share_delta_points,
                                  ),
                                fontWeight: 850,
                              }}
                            >
                              {formatSignedPoints(
                                row.revenue_share_delta_points,
                              )}
                            </Typography>
                          </TableCell>

                          <TableCell align="right">
                            <Button
                              size="small"
                              variant="text"
                              endIcon={
                                <OpenInNewRoundedIcon
                                  sx={{
                                    fontSize: 14,
                                  }}
                                />
                              }
                              onClick={() =>
                                openRelationship(
                                  row.customer_id,
                                )
                              }
                              sx={{
                                textTransform: 'none',
                              }}
                            >
                              Examinar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ),
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {!detailQuery.isError
            && data?.status === 'ok'
            && detail === 'activity_rotation'
            && rotationSummary && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm:
                      'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 0.75,
                }}
              >
                {[
                  {
                    bucket: 'current_only' as const,
                    label: 'Solo actuales',
                    value:
                      rotationSummary
                        .entered_current_customers,
                    revenue:
                      rotationSummary
                        .entered_current_revenue,
                    color: '#34d399',
                  },
                  {
                    bucket: 'comparison_only' as const,
                    label: 'Solo comparados',
                    value:
                      rotationSummary
                        .missing_current_customers,
                    revenue:
                      rotationSummary
                        .missing_comparison_revenue,
                    color: '#fb7185',
                  },
                ].map((item) => {
                  const selected =
                    activeRotationBucket
                    === item.bucket;

                  return (
                    <Button
                      key={item.bucket}
                      variant="outlined"
                      aria-pressed={selected}
                      onClick={() => {
                        setActiveRotationBucket(
                          item.bucket,
                        );
                        resetPagination();
                      }}
                      sx={{
                        px: 1.5,
                        py: 1,
                        minHeight: 72,
                        justifyContent:
                          'space-between',
                        textTransform: 'none',
                        borderRadius: 2,
                        borderColor: selected
                          ? '#38bdf8'
                          : 'rgba(148,163,184,0.16)',
                        bgcolor: selected
                          ? 'rgba(56,189,248,0.10)'
                          : 'rgba(15,23,42,0.26)',
                        color: '#e2e8f0',

                        '&:hover': {
                          borderColor:
                            '#38bdf8',
                          bgcolor:
                            'rgba(56,189,248,0.08)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: 'left',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: selected
                              ? '#f8fafc'
                              : '#94a3b8',
                            fontWeight: 800,
                          }}
                        >
                          {item.label}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.2,
                            color: '#64748b',
                          }}
                        >
                          {formatCurrency(
                            item.revenue,
                            currencyCode,
                          )}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        sx={{
                          color: item.color,
                          fontWeight: 900,
                          lineHeight: 1,
                        }}
                      >
                        {formatInteger(
                          item.value,
                        )}
                      </Typography>
                    </Button>
                  );
                })}

                <Box
                  sx={{
                    px: 1.5,
                    py: 1,
                    minHeight: 72,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent:
                      'space-between',
                    borderRadius: 2,
                    border:
                      '1px solid rgba(148,163,184,0.16)',
                    bgcolor:
                      'rgba(15,23,42,0.26)',
                  }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#94a3b8',
                        fontWeight: 800,
                      }}
                    >
                      Saldo de actividad
                    </Typography>

                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                      }}
                    >
                      clientes
                    </Typography>
                  </Box>

                  <Typography
                    variant="h6"
                    sx={{
                      color: movementColor(
                        rotationSummary
                          .net_active_customer_change,
                      ),
                      fontWeight: 900,
                    }}
                  >
                    {rotationSummary
                      .net_active_customer_change > 0
                      ? '+'
                      : ''}{formatInteger(
                      rotationSummary
                        .net_active_customer_change,
                    )}
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.45,
                }}
              >
                La ausencia se evalúa al cierre del periodo sin actividad y contra el histórico disponible hasta esa fecha. Superar el P90 indica que la ausencia está fuera del intervalo histórico P90 de la relación; no implica por sí sola pérdida, abandono ni sustitución.
              </Typography>

              <TableContainer
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  border:
                    '1px solid rgba(148,163,184,0.14)',
                  borderRadius: 2.5,
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: 1180,

                    '& .MuiTableCell-head': {
                      bgcolor: '#111827',
                      color: '#e2e8f0',
                      fontWeight: 850,
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Cliente
                      </TableCell>

                      <TableCell align="right">
                        Actividad del tramo
                      </TableCell>

                      <TableCell>
                        Cadencia habitual
                      </TableCell>

                      <TableCell>
                        Ausencia vs patrón
                      </TableCell>

                      <TableCell>
                        Tamaño de compra
                      </TableCell>

                      <TableCell align="right">
                        Acción
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {(rotationRows ?? []).map(
                      (row) => {
                        const cadence =
                          getRotationCadencePresentation(
                            row,
                          );

                        const absence =
                          getRotationAbsencePresentation(
                            row,
                          );

                        const purchaseSize =
                          getRotationPurchaseSizePresentation(
                            row,
                          );

                        return (
                          <TableRow
                            key={row.customer_id}
                          >
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#f8fafc',
                                  fontWeight: 800,
                                }}
                              >
                                {row.label}
                              </Typography>
                            </TableCell>

                            <TableCell
                              align="right"
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#e2e8f0',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatCurrency(
                                  row.revenue,
                                  currencyCode,
                                )} · {formatPct(
                                  row.revenue_share_pct,
                                )}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.15,
                                  color: '#64748b',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {formatInteger(
                                  row.transaction_count,
                                )} trans. · {
                                  row.first_activity_date
                                  ?? '—'
                                } → {
                                  row.last_activity_date
                                  ?? '—'
                                }
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#e2e8f0',
                                  fontWeight: 800,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {cadence.primary}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.15,
                                  color: '#64748b',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {cadence.secondary}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color:
                                    absence.color,
                                  fontWeight: 850,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {absence.primary}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.15,
                                  color:
                                    absence.color,
                                  opacity: 0.82,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {absence.secondary}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: '#e2e8f0',
                                  fontWeight: 850,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {purchaseSize.primary}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.1,
                                  color: '#94a3b8',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {purchaseSize.secondary}
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  mt: 0.1,
                                  color: '#64748b',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {purchaseSize.detail}
                              </Typography>
                            </TableCell>

                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="text"
                                endIcon={
                                  <OpenInNewRoundedIcon
                                    sx={{
                                      fontSize: 14,
                                    }}
                                  />
                                }
                                onClick={() =>
                                  openRelationship(
                                    row.customer_id,
                                  )
                                }
                                sx={{
                                  textTransform:
                                    'none',
                                }}
                              >
                                Examinar
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      },
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {data?.status === 'ok' && (
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              spacing={1}
              alignItems={{
                xs: 'stretch',
                sm: 'center',
              }}
              justifyContent="space-between"
            >
              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                }}
              >
                {data.page.returned_count} de {data.page.total_count} clientes en este desglose.
              </Typography>

              <Stack
                direction="row"
                spacing={1}
              >
                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    cursorHistory.length === 0
                    || detailQuery.isFetching
                  }
                  onClick={previousPage}
                >
                  Anterior
                </Button>

                <Button
                  size="small"
                  variant="outlined"
                  disabled={
                    !data.page.has_more
                    || detailQuery.isFetching
                  }
                  onClick={nextPage}
                >
                  Siguiente
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
